import { Observable, of } from 'rxjs';
import { map, switchMap, take, tap } from 'rxjs/operators';

import { Result } from '../interfaces/helpers';
import { IEntitlement, IPermission, IUser, Permission } from '../models';
import { CentralApis, Endpoint, PageConfig } from '../interfaces/internal';
import { PageTitles } from '../interfaces/types';
import { Page } from './page';
import { PermissionDTO } from '../interfaces/dtos';

export class PermissionPage extends Page<Permission> {
  permissionUrl: string;

  constructor({ endpoint }: Endpoint, config: PageConfig) {
    super(
      (endpoint as CentralApis).iam + 'permission',
      PageTitles.user,
      config
    );
  }

  addRole(permission: PermissionDTO): Observable<Permission> {
    return this.dataService
      .postDTO<IPermission, PermissionDTO>(this.permissionUrl, permission)
      .pipe(map((result) => this.createFromDTO(result)));
  }

  getPermission(userName: string): Observable<IEntitlement> {
    return this.dataService
      .get<IEntitlement>(this.url, { userName })
      .pipe(map((result) => result));
  }

  fetch(queryString?): Observable<Result<Permission>> {
    return this.getItems(queryString).pipe(
      tap(({ items }) => {
        this.pageItems.next(items);
      })
    );
  }
  updateRole(
    permission: PermissionDTO,
    privilegeID
  ): Observable<PermissionDTO> {
    return this.dataService
      .put<PermissionDTO>(this.permissionUrl + '/' + privilegeID, permission)
      .pipe(map((result) => result));
  }

  fetchById(id: string): Observable<Permission> {
    return this.getItem(id).pipe(
      map((data: IUser) => this.createFromDTO(data))
    );
  }

  update(user: Permission): Observable<Permission> {
    let updatedPermissions: Permission[];

    return this.items.pipe(
      take(1),
      switchMap((users) => {
        if (!users || users.length <= 0) {
          return this.fetch().pipe(map(({ items }) => items));
        } else {
          return of(users);
        }
      }),
      switchMap((users) => {
        const userIndex = users.findIndex((i) => i.id === user.id);
        updatedPermissions = [...users];
        updatedPermissions[userIndex] = this.createFromDTO(user);
        return this.updateItem(
          {
            userName: updatedPermissions[userIndex].userName,
          },
          updatedPermissions[userIndex].id
        );
      }),
      tap(() => {
        this.pageItems.next(updatedPermissions);
      })
    );
  }

  createFromDTO(item): Permission {
    return new Permission(item);
  }
}
